/**
 * ThemeToggle — Phase 1a
 *
 * Two segmented radio-group controls in a single component:
 *   1. Theme: System | Light | Dark
 *   2. Density: Cozy | Compact
 *
 * ARIA pattern: role="radiogroup" with role="radio" buttons. ArrowRight/
 * ArrowLeft navigate between options; Enter/Space activate the focused option.
 * The active option carries aria-checked="true"; inactive options carry "false".
 *
 * density and onDensityChange are optional — when omitted the density
 * segment is hidden. This keeps usages that only care about theme simple.
 */

import type { ThemePreference, Density } from '../types/theme.js';

interface ThemeToggleProps {
	preference: ThemePreference;
	onPreferenceChange: (p: ThemePreference) => void;
	density?: Density;
	onDensityChange?: (d: Density) => void;
}

// ---------------------------------------------------------------------------
// Theme segment
// ---------------------------------------------------------------------------

const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
	{ value: 'system', label: 'System' },
	{ value: 'light', label: 'Light' },
	{ value: 'dark', label: 'Dark' },
];

// ---------------------------------------------------------------------------
// Density segment
// ---------------------------------------------------------------------------

const DENSITY_OPTIONS: { value: Density; label: string }[] = [
	{ value: 'cozy', label: 'Cozy' },
	{ value: 'compact', label: 'Compact' },
];

// ---------------------------------------------------------------------------
// Generic segmented control helper
// ---------------------------------------------------------------------------

interface SegmentedOption<T extends string> {
	value: T;
	label: string;
}

interface SegmentedGroupProps<T extends string> {
	options: SegmentedOption<T>[];
	active: T;
	onChange: (v: T) => void;
	ariaLabel: string;
}

function SegmentedGroup<T extends string>({
	options,
	active,
	onChange,
	ariaLabel,
}: SegmentedGroupProps<T>) {
	const handleKeyDown = (
		e: React.KeyboardEvent<HTMLButtonElement>,
		index: number,
	) => {
		const total = options.length;
		let next = index;

		if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
			next = (index + 1) % total;
		} else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
			next = (index - 1 + total) % total;
		} else if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			onChange(options[index].value);
			return;
		} else {
			return;
		}

		// Move focus to the adjacent button
		const group = (e.currentTarget as HTMLElement).parentElement;
		if (group) {
			const buttons = group.querySelectorAll<HTMLButtonElement>('[role="radio"]');
			buttons[next]?.focus();
		}
	};

	return (
		<div
			role="radiogroup"
			aria-label={ariaLabel}
			className="theme-toggle__group"
		>
			{options.map((opt, idx) => (
				<button
					key={opt.value}
					role="radio"
					aria-checked={active === opt.value ? 'true' : 'false'}
					type="button"
					className={`theme-toggle__option${active === opt.value ? ' theme-toggle__option--active' : ''}`}
					onClick={() => {
						if (opt.value !== active) {
							onChange(opt.value);
						}
					}}
					onKeyDown={(e) => handleKeyDown(e, idx)}
					tabIndex={active === opt.value ? 0 : -1}
				>
					{opt.label}
				</button>
			))}
		</div>
	);
}

// ---------------------------------------------------------------------------
// ThemeToggle
// ---------------------------------------------------------------------------

export default function ThemeToggle({
	preference,
	onPreferenceChange,
	density,
	onDensityChange,
}: ThemeToggleProps) {
	const showDensity = density !== undefined && onDensityChange !== undefined;

	return (
		<div className="theme-toggle">
			<SegmentedGroup
				options={THEME_OPTIONS}
				active={preference}
				onChange={onPreferenceChange}
				ariaLabel="Theme"
			/>

			{showDensity && (
				<SegmentedGroup
					options={DENSITY_OPTIONS}
					active={density}
					onChange={onDensityChange}
					ariaLabel="Density"
				/>
			)}
		</div>
	);
}
